/**
 * Bonne Nuit theme behaviors: order tracking dropdown, category carousel,
 * marquee pause helpers, mobile menu, scroll-to-top, predictive search.
 */
(function () {
  'use strict';

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  /* ---------- Order tracking dropdown ---------- */
  function initOrderTracking() {
    qsa('[data-bn-order-toggle]').forEach(function (btn) {
      var panelId = btn.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;

      var captchaRoot = qs('[data-bn-captcha-root]', panel);
      var captchaApi = null;
      function ensureCaptcha() {
        if (captchaApi || !window.BNOrderTrack || !captchaRoot) return captchaApi;
        captchaApi = window.BNOrderTrack.bindCaptcha(captchaRoot);
        return captchaApi;
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var open = panel.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) ensureCaptcha();
      });

      var form = qs('[data-bn-order-dropdown-form]', panel);
      if (form && form.dataset.bnCaptchaBound !== 'true') {
        form.dataset.bnCaptchaBound = 'true';
        form.addEventListener('submit', function (e) {
          var api = ensureCaptcha();
          var values = api ? api.getValues() : { token: '', answer: '' };
          if (!values.token || !values.answer) {
            e.preventDefault();
            alert('Lütfen güvenlik sorusunu cevaplayın.');
            if (api) api.load();
            return;
          }
          // Native GET carries captcha_token + captcha_answer to the tracking page.
        });
      }
    });

    document.addEventListener('click', function (e) {
      qsa('.bn-order-dropdown.is-open').forEach(function (panel) {
        if (!panel.contains(e.target) && !e.target.closest('[data-bn-order-toggle]')) {
          panel.classList.remove('is-open');
          var toggle = document.querySelector('[aria-controls="' + panel.id + '"]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  /* ---------- Mobile menu drawer ---------- */
  function initMobileMenu() {
    qsa('[data-bn-header]').forEach(function (header) {
      if (header.dataset.bnMenuBound === 'true') return;
      header.dataset.bnMenuBound = 'true';

      var openBtn = qs('[data-bn-menu-open]', header);
      var drawer = qs('[data-bn-menu-drawer]', header);
      var overlay = qs('[data-bn-menu-overlay]', header);
      var headerBar = qs('[data-bn-header-bar]', header);
      var scrollEl = qs('[data-bn-menu-scroll]', drawer) || drawer;
      var moreEl = qs('[data-bn-menu-more]', drawer);
      if (!drawer || !openBtn) return;

      function syncMenuPosition() {
        if (!headerBar) return;
        var bottom = headerBar.getBoundingClientRect().bottom;
        document.documentElement.style.setProperty('--bn-menu-top', bottom + 'px');
      }

      function syncMenuMore() {
        if (!moreEl || !scrollEl) return;
        var canScroll = scrollEl.scrollHeight > scrollEl.clientHeight + 8;
        var nearBottom = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 12;
        var show = canScroll && !nearBottom;
        moreEl.hidden = !show;
        moreEl.setAttribute('aria-hidden', show ? 'false' : 'true');
        drawer.classList.toggle('has-more', show);
      }

      function open() {
        syncMenuPosition();
        header.classList.add('bn-header--menu-open');
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        if (overlay) overlay.classList.add('is-open');
        openBtn.setAttribute('aria-expanded', 'true');
        openBtn.setAttribute('aria-label', 'Menüyü kapat');
        document.documentElement.classList.add('bn-menu-open');
        requestAnimationFrame(function () {
          syncMenuMore();
        });
      }

      function close() {
        header.classList.remove('bn-header--menu-open');
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        if (overlay) overlay.classList.remove('is-open');
        openBtn.setAttribute('aria-expanded', 'false');
        openBtn.setAttribute('aria-label', 'Menüyü aç');
        document.documentElement.classList.remove('bn-menu-open');
        if (moreEl) {
          moreEl.hidden = true;
          moreEl.setAttribute('aria-hidden', 'true');
        }
        drawer.classList.remove('has-more');
      }

      scrollEl.addEventListener(
        'scroll',
        function () {
          if (drawer.classList.contains('is-open')) syncMenuMore();
        },
        { passive: true }
      );

      function toggleMenu(e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        if (drawer.classList.contains('is-open')) close();
        else open();
      }

      openBtn.addEventListener('click', toggleMenu);

      if (overlay) overlay.addEventListener('click', close);

      document.addEventListener('click', function (e) {
        if (!drawer.classList.contains('is-open')) return;
        if (drawer.contains(e.target) || openBtn.contains(e.target)) return;
        close();
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
      });

      window.addEventListener('resize', function () {
        syncMenuPosition();
        if (drawer.classList.contains('is-open')) syncMenuMore();
      });
      window.addEventListener(
        'scroll',
        function () {
          if (drawer.classList.contains('is-open')) syncMenuPosition();
        },
        { passive: true }
      );
    });
  }

  /* ---------- Predictive search ---------- */
  function initPredictiveSearch() {
    var placeholderSrc = window.__bnPlaceholderLogo || '';

    qsa('[data-bn-predictive-search]').forEach(function (root) {
      var input = qs('[data-bn-search-input]', root);
      var results = qs('[data-bn-search-results]', root);
      if (!input || !results) return;
      // Header re-renders in the editor call this again; don't stack listeners.
      if (root.dataset.bnPredictiveBound === 'true') return;
      root.dataset.bnPredictiveBound = 'true';

      var timer = null;
      var controller = null;

      function hideResults() {
        results.hidden = true;
        results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
      }

      function renderProducts(products) {
        if (!products.length) {
          results.innerHTML = '<p class="bn-predictive-search-dropdown__empty">Sonuç bulunamadı.</p>';
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
          return;
        }

        var html = '<div class="bn-predictive-search-dropdown"><ul class="bn-predictive-search-dropdown__list list-unstyled">';
        products.forEach(function (product) {
          var image = '';
          if (product.featured_image && product.featured_image.url) {
            image = product.featured_image.url;
          } else if (typeof product.image === 'string') {
            image = product.image;
          } else if (product.image && product.image.url) {
            image = product.image.url;
          }

          var imageHtml = image
            ? '<img src="' + image + '" alt="" class="bn-predictive-search-dropdown__thumb" width="48" height="48" loading="lazy">'
            : '<img src="' + placeholderSrc + '" alt="" class="bn-predictive-search-dropdown__thumb bn-predictive-search-dropdown__thumb--placeholder" width="48" height="48" loading="lazy">';

          html +=
            '<li><a href="' +
            product.url +
            '" class="bn-predictive-search-dropdown__item">' +
            imageHtml +
            '<span class="bn-predictive-search-dropdown__title">' +
            product.title +
            '</span></a></li>';
        });
        html += '</ul></div>';

        results.innerHTML = html;
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }

      function fetchResults(term) {
        if (controller) controller.abort();
        controller = new AbortController();

        var url =
          '/search/suggest.json?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=hide';

        fetch(url, { signal: controller.signal })
          .then(function (res) {
            return res.json();
          })
          .then(function (data) {
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            renderProducts(products);
          })
          .catch(function (err) {
            if (err.name !== 'AbortError') hideResults();
          });
      }

      input.addEventListener('input', function () {
        var term = input.value.trim();
        clearTimeout(timer);

        if (!term.length) {
          hideResults();
          return;
        }

        timer = setTimeout(function () {
          fetchResults(term);
        }, 200);
      });

      input.addEventListener('focus', function () {
        if (input.value.trim().length) fetchResults(input.value.trim());
      });

      document.addEventListener('click', function (e) {
        if (!root.contains(e.target)) hideResults();
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') hideResults();
      });
    });
  }

  /* ---------- Mobile search toggle ---------- */
  function initMobileSearch() {
    qsa('[data-bn-header]').forEach(function (header) {
      if (header.dataset.bnMobileSearchBound === 'true') return;
      header.dataset.bnMobileSearchBound = 'true';

      var toggle = qs('[data-bn-search-toggle]', header);
      var panel = qs('[data-bn-search-panel]', header);
      if (!toggle || !panel) return;
      var input = qs('[data-bn-search-input]', panel);

      function setOpen(open) {
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open && input) input.focus();
      }

      // No stopPropagation: the menu drawer's own document listener should still
      // see this click and close the drawer when search opens.
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        setOpen(panel.hidden);
      });

      document.addEventListener('click', function (e) {
        if (panel.hidden) return;
        if (panel.contains(e.target) || toggle.contains(e.target)) return;
        setOpen(false);
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !panel.hidden) {
          setOpen(false);
          toggle.focus();
        }
      });
    });
  }

  /* ---------- Logo fallback for broken CDN uploads ---------- */
  function initLogoFallback() {
    qsa('[data-bn-logo-fallback]').forEach(function (img) {
      var fallback = img.getAttribute('data-bn-logo-fallback');
      if (!fallback) return;

      img.addEventListener('error', function () {
        if (img.dataset.bnLogoFailed === 'true') return;
        img.dataset.bnLogoFailed = 'true';
        img.removeAttribute('srcset');
        img.src = fallback;
      });
    });
  }

  /* ---------- Category carousel ---------- */
  function initCarousels() {
    qsa('[data-bn-carousel]').forEach(function (root) {
      if (root.dataset.bnCarouselBound === 'true') return;
      root.dataset.bnCarouselBound = 'true';

      var track = qs('[data-bn-carousel-track]', root);
      var slides = qsa('[data-bn-carousel-slide]', root);
      if (!track || slides.length === 0) return;

      var prev = qs('[data-bn-carousel-prev]', root);
      var next = qs('[data-bn-carousel-next]', root);
      var autoplay = root.getAttribute('data-autoplay') !== 'false';
      var interval = parseInt(root.getAttribute('data-interval') || '2500', 10);
      var index = 0;
      var timer = null;
      var pointerStartX = null;

      function perView() {
        return window.matchMedia('(max-width: 767px)').matches
          ? parseInt(root.getAttribute('data-mobile') || '1', 10)
          : parseInt(root.getAttribute('data-desktop') || '2', 10);
      }

      function maxIndex() {
        return Math.max(0, slides.length - perView());
      }

      function go(i) {
        index = Math.max(0, Math.min(i, maxIndex()));
        var slideWidth = slides[0].getBoundingClientRect().width;
        var gap = parseFloat(getComputedStyle(track).gap) || 0;
        track.style.transform = 'translateX(-' + index * (slideWidth + gap) + 'px)';
      }

      function nextSlide() {
        if (index >= maxIndex()) go(0);
        else go(index + perView());
      }

      function prevSlide() {
        if (index <= 0) go(maxIndex());
        else go(index - perView());
      }

      function start() {
        if (!autoplay || slides.length <= perView()) return;
        stop();
        timer = setInterval(nextSlide, interval);
      }

      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }

      if (prev) prev.addEventListener('click', function () { prevSlide(); start(); });
      if (next) next.addEventListener('click', function () { nextSlide(); start(); });
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);
      root.addEventListener('pointerdown', function (event) {
        pointerStartX = event.clientX;
        stop();
      });
      root.addEventListener('pointerup', function (event) {
        if (pointerStartX === null) return;
        var distance = event.clientX - pointerStartX;
        pointerStartX = null;
        if (Math.abs(distance) > 40) {
          if (distance < 0) nextSlide();
          else prevSlide();
        }
        start();
      });
      root.addEventListener('pointercancel', function () {
        pointerStartX = null;
        start();
      });
      window.addEventListener('resize', function () { go(index); });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });

      go(0);
      start();
    });
  }

  /* ---------- Scroll to top ---------- */
  function initScrollTop() {
    var btn = qs('[data-bn-scroll-top]');
    if (!btn) return;

    window.addEventListener(
      'scroll',
      function () {
        if (window.scrollY > 400) btn.classList.add('is-visible');
        else btn.classList.remove('is-visible');
      },
      { passive: true }
    );

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ---------- Sticky header ---------- */
  function initStickyHeader() {
    var header = qs('[data-bn-header]');
    var bar = qs('[data-bn-header-bar]');
    if (!header || !bar) return;

    var spacer = document.createElement('div');
    spacer.className = 'bn-header__spacer';
    spacer.setAttribute('aria-hidden', 'true');
    header.appendChild(spacer);

    function sync() {
      var h = bar.offsetHeight;
      spacer.style.height = header.classList.contains('is-sticky') ? h + 'px' : '0px';
      document.documentElement.style.setProperty('--bn-header-sticky-h', h + 'px');
    }

    window.addEventListener(
      'scroll',
      function () {
        if (window.scrollY > 10) header.classList.add('is-sticky');
        else header.classList.remove('is-sticky');
        sync();
      },
      { passive: true }
    );

    window.addEventListener('resize', sync);
    sync();
  }

  /* ---------- Entrance and section reveal motion ---------- */
  function initPageMotion() {
    window.requestAnimationFrame(function () {
      document.body.classList.remove('bn-motion-pending');
      document.body.classList.add('bn-motion-ready');
    });
  }

  function initSectionReveal() {
    /* Disabled: whole-section opacity:0 hid tall homepage blocks. */
  }

  /* ---------- Featured product cards: staggered upward reveal ---------- */
  function initProductCardReveal(root) {
    var scope = root || document;
    var cards = qsa('.bn-featured-products__item.bn-card-reveal', scope);
    if (!cards.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      cards.forEach(function (card) {
        card.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.12 }
    );

    cards.forEach(function (card) {
      if (card.classList.contains('is-visible')) return;
      observer.observe(card);
    });
  }

  /* ---------- Product page gallery zoom ---------- */
  function initGalleryZoom(gallery) {
    var stage = qs('.bn-product-gallery__stage', gallery);
    if (!stage) return function () {};

    var mql = window.matchMedia('(hover: hover) and (min-width: 992px)');

    var state = {
      zoomImg: null,
      slide: null,
      factorX: 0,
      factorY: 0,
      displayW: 0,
      displayH: 0
    };

    function clearZoom() {
      if (state.zoomImg) {
        state.zoomImg.remove();
        state.zoomImg = null;
      }
      if (state.slide) {
        state.slide.classList.remove('is-zooming');
      }
      state.slide = null;
    }

    function computeFactors() {
      if (!state.zoomImg || !state.slide) return false;

      state.displayW = state.slide.offsetWidth;
      state.displayH = state.slide.offsetHeight;
      if (!state.displayW || !state.displayH) return false;

      var zoomW = state.zoomImg.offsetWidth;
      var zoomH = state.zoomImg.offsetHeight;
      if (zoomW <= state.displayW || zoomH <= state.displayH) {
        clearZoom();
        return false;
      }

      state.factorX = (zoomW - state.displayW) / state.displayW;
      state.factorY = (zoomH - state.displayH) / state.displayH;
      return true;
    }

    function moveZoom(clientX, clientY) {
      if (!state.zoomImg || !state.slide) return;

      var rect = state.slide.getBoundingClientRect();
      var x = Math.max(0, Math.min(clientX - rect.left, state.displayW));
      var y = Math.max(0, Math.min(clientY - rect.top, state.displayH));

      state.zoomImg.style.left = -x * state.factorX + 'px';
      state.zoomImg.style.top = -y * state.factorY + 'px';
    }

    function startZoom(slide, baseImg, clientX, clientY) {
      var zoomSrc = baseImg.getAttribute('data-zoom-src') || baseImg.currentSrc || baseImg.src;
      if (!zoomSrc) return;

      clearZoom();
      state.slide = slide;

      var zoomImg = document.createElement('img');
      zoomImg.className = 'bn-product-gallery__zoom-img';
      zoomImg.alt = '';
      zoomImg.src = zoomSrc;

      zoomImg.addEventListener('load', function () {
        zoomImg.style.width = zoomImg.naturalWidth + 'px';
        zoomImg.style.height = zoomImg.naturalHeight + 'px';
        if (!computeFactors()) return;
        slide.classList.add('is-zooming');
        moveZoom(clientX, clientY);
      });

      slide.appendChild(zoomImg);
      state.zoomImg = zoomImg;
    }

    function syncZoomMode() {
      if (mql.matches) {
        stage.classList.add('bn-product-gallery__stage--zoom');
      } else {
        stage.classList.remove('bn-product-gallery__stage--zoom');
        clearZoom();
      }
    }

    stage.addEventListener('mousemove', function (e) {
      if (!mql.matches) {
        clearZoom();
        return;
      }

      if (e.target.closest('.bn-product-gallery__nav')) {
        clearZoom();
        return;
      }

      var slide = qs('.bn-product-gallery__slide.is-active', stage);
      if (!slide) return;

      var baseImg = qs('.bn-product-gallery__img:not(.bn-product-gallery__img--placeholder)', slide);
      if (!baseImg) return;

      if (!state.zoomImg || state.slide !== slide) {
        startZoom(slide, baseImg, e.clientX, e.clientY);
      } else if (slide.classList.contains('is-zooming')) {
        moveZoom(e.clientX, e.clientY);
      }
    });

    stage.addEventListener('mouseleave', clearZoom);
    window.addEventListener('resize', clearZoom);

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', syncZoomMode);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(syncZoomMode);
    }

    syncZoomMode();

    return clearZoom;
  }

  /* ---------- Product page gallery, variants, add to cart modal ---------- */
  function initProductPage() {
    qsa('[data-bn-product]').forEach(function (root) {
      var jsonEl = qs('[data-bn-product-json]', root);
      var form = qs('[data-bn-product-form]', root);
      if (!jsonEl || !form) return;

      var product = null;
      try {
        product = JSON.parse(jsonEl.textContent || '{}');
      } catch (e) {
        return;
      }

      var variantInput = qs('[data-bn-variant-id]', form);
      var priceWhole = qs('[data-bn-price-whole]', root);
      var priceFrac = qs('[data-bn-price-frac]', root);
      var priceCompare = qs('[data-bn-price-compare]', root);
      var submitBtn = qs('[data-bn-add-to-cart]', form);
      var errorEl = qs('[data-bn-product-error]', form);
      var modal = qs('[data-bn-cart-modal]', root);
      if (modal && modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
      var optionSelects = qsa('[data-bn-option-select]', form);
      var moneyFormat = window.__bnMoneyFormat || '${{amount}}';

      function formatMoney(cents) {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents, moneyFormat);
        }
        return (cents / 100).toFixed(2).replace('.', ',') + ' TL';
      }

      function splitPrice(cents) {
        var whole = Math.floor(cents / 100);
        var decimal = String(cents % 100).padStart(2, '0');
        return {
          whole: String(whole),
          frac: ',' + decimal + ' TL'
        };
      }

      function findVariant() {
        if (!product.variants || !product.variants.length) return null;
        if (optionSelects.length === 0) return product.variants[0];

        var selected = optionSelects.map(function (select) {
          return select.value;
        });

        return product.variants.find(function (variant) {
          return variant.options.every(function (opt, idx) {
            return opt === selected[idx];
          });
        });
      }

      function updateVariantUI(variant) {
        if (!variant || !variantInput) return;

        variantInput.value = variant.id;
        var split = splitPrice(variant.price);
        if (priceWhole) priceWhole.textContent = split.whole;
        if (priceFrac) priceFrac.textContent = split.frac;
        if (priceWhole && priceWhole.closest('[data-bn-product-price]')) {
          priceWhole
            .closest('[data-bn-product-price]')
            .setAttribute('data-bn-price-cents', String(variant.price));
        }

        if (priceCompare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceCompare.hidden = false;
            priceCompare.textContent = formatMoney(variant.compare_at_price);
          } else {
            priceCompare.hidden = true;
            priceCompare.textContent = '';
          }
        }

        if (submitBtn) {
          submitBtn.textContent = variant.available
            ? submitBtn.getAttribute('data-label-available') || 'Sepete Ekle!'
            : submitBtn.getAttribute('data-label-soldout') || 'Tükendi';
          // Don't fight the scheduled-delivery widget gate — only force-disable
          // when the variant itself is unavailable.
          if (!variant.available) {
            submitBtn.disabled = true;
          } else {
            document.dispatchEvent(new CustomEvent('bn:variant-price', {
              detail: { cents: variant.price, available: true }
            }));
          }
        }
      }

      updateVariantUI(findVariant());

      optionSelects.forEach(function (select) {
        select.addEventListener('change', function () {
          updateVariantUI(findVariant());
        });
      });

      var editKey = getBnEditKey();
      if (editKey) {
        if (submitBtn) {
          submitBtn.textContent = 'Güncelle';
          submitBtn.setAttribute('data-label-available', 'Güncelle');
        }
        fetch(rootPath() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (res) {
            if (!res.ok) throw new Error('cart_get_failed');
            return res.json();
          })
          .then(function (cart) {
            var items = (cart && cart.items) || [];
            var line = null;
            for (var i = 0; i < items.length; i++) {
              if (items[i].key === editKey) {
                line = items[i];
                break;
              }
            }
            if (!line) {
              clearBnEditParam();
              return;
            }

            var snapshot = snapshotFromCartLine(line);
            prefillProductFormFromLine(form, product, line, snapshot);
            updateVariantUI(findVariant());

            var addonCandidates = [];
            items.forEach(function (it) {
              if (!it || it.key === editKey) return;
              if (isFeeLineItem(it)) return;
              if (hasScheduledDeliveryProps(it.properties)) return;
              addonCandidates.push({
                key: it.key,
                variantId: it.variant_id,
                // Carries "Rakam" so editing a line keeps the number the
                // shopper already typed for a rakam add-on.
                properties: it.properties || null
              });
            });
            BnCart.beginEdit(editKey, addonCandidates);

            // Persist snapshot so SCD can restore even if its listener
            // registers after this event (script load order).
            window.__bnScdPendingRestore = snapshot;
            if (window.BnScd && typeof window.BnScd.restore === 'function') {
              window.BnScd.restore(snapshot);
            } else {
              document.dispatchEvent(
                new CustomEvent('bn:scd:restore', { detail: snapshot })
              );
            }

            // Message card / custom fields may mount after widget config loads.
            document.addEventListener(
              'bn:scd:options-ready',
              function onOptionsReady() {
                prefillProductFormFromLine(form, product, line, snapshot);
                document.removeEventListener('bn:scd:options-ready', onOptionsReady);
              }
            );

            clearBnEditParam();
          })
          .catch(function (err) {
            console.warn('[BN] cart edit restore failed', err);
            clearBnEditParam();
          });
      }

      var gallery = qs('[data-bn-product-gallery]', root);
      if (gallery) {
        var slides = qsa('[data-bn-gallery-slide]', gallery);
        var thumbs = qsa('[data-bn-gallery-thumb]', gallery);
        var current = 0;
        var clearGalleryZoom = initGalleryZoom(gallery);

        function showSlide(index) {
          if (!slides.length) return;
          current = (index + slides.length) % slides.length;
          if (clearGalleryZoom) clearGalleryZoom();
          slides.forEach(function (slide, i) {
            slide.classList.toggle('is-active', i === current);
          });
          thumbs.forEach(function (thumb, i) {
            thumb.classList.toggle('is-active', i === current);
          });
        }

        var prevBtn = qs('[data-bn-gallery-prev]', gallery);
        var nextBtn = qs('[data-bn-gallery-next]', gallery);
        if (prevBtn) prevBtn.addEventListener('click', function () { showSlide(current - 1); });
        if (nextBtn) nextBtn.addEventListener('click', function () { showSlide(current + 1); });
        thumbs.forEach(function (thumb) {
          thumb.addEventListener('click', function () {
            showSlide(parseInt(thumb.getAttribute('data-index') || '0', 10));
          });
        });
      }

      function updateCartBadges(count) {
        qsa('.bn-header__badge').forEach(function (badge) {
          if (count > 0) {
            badge.textContent = String(count);
            badge.hidden = false;
          } else {
            badge.hidden = true;
          }
        });
      }

      function openModal() {
        if (!modal) return;
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('bn-cart-modal-open');
      }

      function closeModal() {
        if (!modal) return;
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('bn-cart-modal-open');
      }

      if (modal) {
        qsa('[data-bn-cart-modal-close]', modal).forEach(function (el) {
          el.addEventListener('click', closeModal);
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && !modal.hidden) closeModal();
        });
      }

      initMessageCard(form);

      // Photo uploads (app-driven slots may appear after widget config loads).
      initProductPhotos(form);
      document.addEventListener('bn:photos-ready', function () {
        initProductPhotos(form);
      });

      form.addEventListener('submit', function (e) {
        // Scheduled delivery owns PDP add-to-cart (capture listener). If we
        // reach bubble phase with SCD required, do not AJAX-add without booking.
        var scd = document.querySelector('[data-scd-widget]:not([hidden])');
        if (scd && scd.getAttribute('data-scd-require') !== 'false') {
          e.preventDefault();
          return;
        }

        e.preventDefault();
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }

        var variant = findVariant();
        if (!variant || !variant.available) return;

        // Required custom fields (e.g. confirm checkbox) must be satisfied.
        var requiredEls = form.querySelectorAll('[data-bn-required]');
        for (var ri = 0; ri < requiredEls.length; ri++) {
          var rel = requiredEls[ri];
          var ok = (rel.type === 'checkbox' || rel.type === 'radio')
            ? rel.checked
            : String(rel.value || '').trim().length > 0;
          if (!ok) {
            var cerr = form.querySelector('[data-bn-custom-error]');
            if (cerr) cerr.hidden = false;
            return;
          }
        }

        // Collect line item properties (custom message, confirm, etc.).
        var lineProps = {};
        var propInputs = form.querySelectorAll('[name^="properties["]');
        Array.prototype.forEach.call(propInputs, function (el) {
          var m = el.name.match(/^properties\[(.+)\]$/);
          if (!m) return;
          var key = m[1];
          if (el.type === 'checkbox' || el.type === 'radio') {
            if (el.checked && String(el.value || '').trim()) lineProps[key] = el.value;
          } else {
            var v = String(el.value || '').trim();
            if (v) lineProps[key] = v;
          }
        });

        if (submitBtn) submitBtn.disabled = true;

        // Include checked add-on variants (populated by delivery widget).
        var items = [{
          id: variant.id,
          quantity: 1,
          properties: Object.keys(lineProps).length ? lineProps : undefined
        }];
        qsa('.bn-addons__cb:checked', form).forEach(function (cb) {
          var row = cb.closest('.bn-addons__item');
          // Widget stores selection in its own map; theme path reads data attr if present.
          var vid = cb.getAttribute('data-variant-id');
          if (vid) items.push({ id: Number(vid), quantity: 1 });
        });

        fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items: items })
        })
          .then(function (res) {
            return res.json().then(function (data) {
              if (!res.ok) throw data;
              return data;
            });
          })
          .then(function () {
            // Edit mode: add new first, then remove the old line(s).
            var replace =
              BnCart.editSession && typeof BnCart.replaceEditLines === 'function'
                ? BnCart.replaceEditLines()
                : Promise.resolve(null);
            return replace.then(function () {
              document.dispatchEvent(
                new CustomEvent('bn:cart:updated', { detail: { open: true } })
              );
            });
          })
          .catch(function (err) {
            if (errorEl) {
              errorEl.hidden = false;
              errorEl.textContent =
                (err && err.description) || (err && err.message) || 'Sepete eklenirken bir hata oluştu.';
            }
          })
          .finally(function () {
            if (submitBtn && variant.available) submitBtn.disabled = false;
          });
      });
    });
  }

  /* ---------- Add-to-cart overlay ---------- */
  function openAtcOverlay(overlay) {
    overlay.hidden = false;
    overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('bn-modal-open');
  }

  function closeAtcOverlay(overlay) {
    overlay.hidden = true;
    overlay.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('bn-modal-open');
  }

  function initAtcOverlay() {
    var overlay = qs('[data-bn-atc-overlay]');
    if (!overlay || overlay.getAttribute('data-bn-bound')) return;
    overlay.setAttribute('data-bn-bound', '1');

    overlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-bn-atc-close]')) {
        e.preventDefault();
        closeAtcOverlay(overlay);
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !overlay.hidden) closeAtcOverlay(overlay);
    });
  }

  /* ---------- Message card popup (note + greeting card) ---------- */
  function initMessageCard(form) {
    var host = qs('[data-bn-message-card]', form);
    if (!host || host.getAttribute('data-bn-bound')) return;

    var modal = qs('[data-bn-message-card-modal]', host);
    var noteStore = qs('[data-bn-message-card-note]', host);
    var nameStore = qs('[data-bn-message-card-name]', host);
    var imageStore = qs('[data-bn-message-card-image]', host);
    var codeStore = qs('[data-bn-message-card-code]', host);
    var signStore = qs('[data-bn-message-card-signature]', host);
    if (!modal || !noteStore || !nameStore || !imageStore) return;
    host.setAttribute('data-bn-bound', '1');

    // The PDP panel animates with transforms, which would make a nested
    // position:fixed modal resolve against the panel instead of the viewport.
    document.body.appendChild(modal);

    var summary = qs('[data-bn-message-card-summary]', host);
    var input = qs('[data-bn-message-card-input]', modal);
    var signInput = qs('[data-bn-message-card-signature-input]', modal);
    var options = qsa('[data-bn-message-card-option]', modal);

    function syncTrigger() {
      var parts = [];
      var card = String(nameStore.value || '').trim();
      var note = String(noteStore.value || '').trim();
      if (card) parts.push(card);
      if (note) parts.push(note);
      host.classList.toggle('is-filled', parts.length > 0);
      if (summary) summary.textContent = parts.length ? parts.join(' — ') : 'Ekle';
    }

    function markSelected(slug) {
      options.forEach(function (btn) {
        var on = !!slug && btn.getAttribute('data-card-slug') === slug;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function selectedOption() {
      for (var i = 0; i < options.length; i++) {
        if (options[i].getAttribute('aria-pressed') === 'true') return options[i];
      }
      return null;
    }

    function open() {
      if (input) input.value = noteStore.value || '';
      if (signInput && signStore) signInput.value = signStore.value || '';
      markSelected(host.getAttribute('data-selected-card'));
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('bn-modal-open');
      if (input) input.focus();
    }

    function close() {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('bn-modal-open');
    }

    function commit() {
      var chosen = selectedOption();
      noteStore.value = input ? String(input.value || '').trim() : '';
      if (signStore) {
        signStore.value = signInput ? String(signInput.value || '').trim() : '';
      }
      nameStore.value = chosen ? chosen.getAttribute('data-card-name') || '' : '';
      imageStore.value = chosen ? chosen.getAttribute('data-card-image') || '' : '';
      if (codeStore) {
        codeStore.value = chosen ? chosen.getAttribute('data-card-code') || '' : '';
      }
      if (chosen) host.setAttribute('data-selected-card', chosen.getAttribute('data-card-slug') || '');
      else host.removeAttribute('data-selected-card');
      syncTrigger();
      close();
    }

    host.addEventListener('click', function (e) {
      if (e.target.closest('[data-bn-message-card-open]')) {
        e.preventDefault();
        open();
      }
    });

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-bn-message-card-close]')) {
        e.preventDefault();
        close();
        return;
      }
      if (e.target.closest('[data-bn-message-card-save]')) {
        e.preventDefault();
        commit();
        return;
      }
      if (e.target.closest('[data-bn-message-card-clear]')) {
        e.preventDefault();
        if (input) input.value = '';
        if (signInput) signInput.value = '';
        markSelected(null);
        return;
      }
      var option = e.target.closest('[data-bn-message-card-option]');
      if (option) {
        e.preventDefault();
        var alreadyOn = option.getAttribute('aria-pressed') === 'true';
        markSelected(alreadyOn ? null : option.getAttribute('data-card-slug'));
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    // Cart "Düzenle" refills the hidden inputs directly; this re-derives the
    // visible selection and trigger label from them.
    host.addEventListener('bn:message-card:sync', function () {
      var name = String(nameStore.value || '').trim();
      var match = null;
      for (var i = 0; i < options.length && name; i++) {
        if (options[i].getAttribute('data-card-name') === name) {
          match = options[i];
          break;
        }
      }
      if (match) host.setAttribute('data-selected-card', match.getAttribute('data-card-slug') || '');
      else host.removeAttribute('data-selected-card');
      if (codeStore) {
        codeStore.value = match ? match.getAttribute('data-card-code') || '' : '';
      }
      syncTrigger();
    });

    syncTrigger();
  }

  function initProductPhotos(form) {
    var host = qs('[data-bn-photos]', form);
    if (!host) return;
    var proxy = host.getAttribute('data-bn-upload-proxy') || '/apps/scheduled-delivery/upload';
    var errEl = qs('[data-bn-photo-error]', host);

    qsa('[data-bn-photo-input]', host).forEach(function (input) {
      if (input.getAttribute('data-bn-photo-bound')) return;
      input.setAttribute('data-bn-photo-bound', '1');
      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        if (!file) return;
        var idx = input.getAttribute('data-bn-photo-index');
        var slot = input.closest('.bn-product-photos__slot');
        var preview = slot ? qs('[data-bn-photo-preview]', slot) : null;
        var placeholder = slot ? qs('[data-bn-photo-placeholder]', slot) : null;
        var urlInput = form.querySelector(
          '[data-bn-photo-url][data-bn-photo-index="' + idx + '"]'
        );

        if (errEl) {
          errEl.hidden = true;
          errEl.textContent = '';
        }

        if (preview) {
          preview.src = URL.createObjectURL(file);
          preview.hidden = false;
        }
        if (placeholder) placeholder.hidden = true;

        var fd = new FormData();
        fd.append('file', file, file.name);

        input.disabled = true;
        fetch(proxy, { method: 'POST', body: fd })
          .then(function (r) {
            return r.json().then(function (j) {
              if (!r.ok || !j.ok) throw j;
              return j;
            });
          })
          .then(function (j) {
            if (urlInput) urlInput.value = j.url || '';
          })
          .catch(function (err) {
            if (urlInput) urlInput.value = '';
            if (preview) {
              preview.hidden = true;
              preview.removeAttribute('src');
            }
            if (placeholder) placeholder.hidden = false;
            if (errEl) {
              errEl.hidden = false;
              errEl.textContent =
                (err && err.message) || 'Fotoğraf yüklenemedi. Lütfen tekrar deneyin.';
            }
          })
          .finally(function () {
            input.disabled = false;
          });
      });
    });
  }

  function initProductCardImages() {
    qsa('[data-bn-product-card-media]').forEach(function (media) {
      var primary = qs('[data-bn-product-card-image="primary"]', media);
      if (!primary) return;

      function markLoaded() {
        media.classList.add('is-loaded');
      }

      if (primary.complete && primary.naturalWidth > 0) {
        markLoaded();
        return;
      }

      primary.addEventListener('load', markLoaded, { once: true });
      primary.addEventListener('error', markLoaded, { once: true });
    });
  }

  function rootPath() {
    return window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
  }

  function isFeeLineItem(item) {
    var type = String((item && item.product_type) || '').toLowerCase();
    var title = String((item && (item.product_title || item.title)) || '').toLowerCase();
    return (
      type.indexOf('teslimat') !== -1 ||
      title.indexOf('teslimat ücreti') !== -1 ||
      title === 'standart teslimat' ||
      title === 'hızlı teslimat'
    );
  }

  function cartEditUrl(item) {
    var base = (item && item.url) || '#';
    var sep = base.indexOf('?') >= 0 ? '&' : '?';
    return base + sep + 'bn_edit=' + encodeURIComponent(item.key);
  }

  function getBnEditKey() {
    try {
      return new URLSearchParams(window.location.search || '').get('bn_edit');
    } catch (e) {
      return null;
    }
  }

  function clearBnEditParam() {
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has('bn_edit')) return;
      url.searchParams.delete('bn_edit');
      var next = url.pathname + url.search + url.hash;
      window.history.replaceState({}, '', next);
    } catch (e) {
      /* ignore */
    }
  }

  function hasScheduledDeliveryProps(props) {
    if (!props) return false;
    return Boolean(
      props._scheduled_delivery_booking_id ||
        props._scheduled_delivery_fulfillment ||
        props._scheduled_delivery_neighborhood_id ||
        props._scheduled_delivery_branch_id ||
        props['Teslimat Tarihi']
    );
  }

  function snapshotFromCartLine(item) {
    var props = (item && item.properties) || {};
    var fulfillment = props._scheduled_delivery_fulfillment || 'delivery';
    var expressRaw = props['Hızlı Teslimat'];
    var placeName = props['Teslimat Mekanı'] || '';
    var placeAddress = props['Teslimat Adresi'] || '';
    var place = null;
    if (placeName || placeAddress) {
      place = {
        name: placeName || placeAddress,
        formattedAddress: placeAddress || placeName
      };
    }
    return {
      fulfillment: fulfillment,
      pickup: fulfillment === 'pickup',
      branchId: props._scheduled_delivery_branch_id || null,
      neighborhoodId: props._scheduled_delivery_neighborhood_id || null,
      neighborhoodName: props['Teslimat Bölgesi'] || props['Teslimat Şubesi'] || '',
      date: props['Teslimat Tarihi'] || null,
      slotStart: props._scheduled_delivery_slot_start || null,
      slotEnd: props._scheduled_delivery_slot_end || null,
      express:
        expressRaw === 'Evet' ||
        expressRaw === 'evet' ||
        expressRaw === 'true' ||
        expressRaw === '1',
      place: place,
      properties: props,
      quantity: (item && item.quantity) || 1,
      variantId: (item && item.variant_id) || null
    };
  }

  function prefillProductFormFromLine(form, product, item, snapshot) {
    if (!form || !item) return;
    var props = (snapshot && snapshot.properties) || item.properties || {};
    var variantId = item.variant_id;
    var variantInput = qs('[data-bn-variant-id]', form) || qs('[name=id]', form);
    if (variantInput && variantId) variantInput.value = String(variantId);

    if (product && product.variants && product.options) {
      var variant = null;
      for (var i = 0; i < product.variants.length; i++) {
        if (Number(product.variants[i].id) === Number(variantId)) {
          variant = product.variants[i];
          break;
        }
      }
      if (variant && variant.options) {
        qsa('[data-bn-option-select]', form).forEach(function (select, idx) {
          if (variant.options[idx] != null) select.value = variant.options[idx];
        });
      }
    }

    var qty = (snapshot && snapshot.quantity) || item.quantity || 1;
    var qtyInput = form.querySelector('[name=quantity]');
    if (!qtyInput) {
      qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'quantity';
      form.appendChild(qtyInput);
    }
    qtyInput.value = String(qty);

    Object.keys(props).forEach(function (key) {
      if (!key || key.charAt(0) === '_') return;
      var val = props[key];
      if (val == null || String(val).trim() === '') return;
      var els = form.querySelectorAll('[name="properties[' + key + ']"]');
      Array.prototype.forEach.call(els, function (el) {
        if (el.type === 'checkbox' || el.type === 'radio') {
          el.checked = String(el.value) === String(val) || val === 'Evet';
        } else {
          el.value = String(val);
        }
      });
    });

    // The card image rides on an underscore-prefixed property, so the visible
    // loop above skips it; restore it explicitly before re-syncing the popup.
    var msgCard = form.querySelector('[data-bn-message-card]');
    var cardImage = form.querySelector('[data-bn-message-card-image]');
    if (cardImage) cardImage.value = String(props['_kart_gorseli'] || '');
    if (msgCard) {
      msgCard.dispatchEvent(new CustomEvent('bn:message-card:sync'));
    }
  }

  function visibleProps(properties, max) {
    var out = [];
    if (!properties) return out;
    var keys = Object.keys(properties);
    var limit = typeof max === 'number' ? max : Infinity;
    for (var i = 0; i < keys.length && out.length < limit; i++) {
      var key = keys[i];
      var val = properties[key];
      if (!key || key.charAt(0) === '_') continue;
      if (val == null || String(val).trim() === '') continue;
      if (String(val).indexOf('/uploads/') !== -1) continue;
      out.push({ key: key, value: String(val) });
    }
    return out;
  }

  function cartConflicts(cart) {
    var hasPickup = false;
    var hasDelivery = false;
    var branches = {};
    var branchCount = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (isFeeLineItem(item)) continue;
      var props = item.properties || {};
      var fulfillment = props._scheduled_delivery_fulfillment || 'delivery';
      var branch = props._scheduled_delivery_branch_id || '';
      if (fulfillment === 'pickup') {
        hasPickup = true;
        if (branch && !branches[branch]) {
          branches[branch] = true;
          branchCount += 1;
        }
      } else {
        hasDelivery = true;
      }
    }
    return {
      fulfillment: hasPickup && hasDelivery,
      store: branchCount > 1
    };
  }

  var BnCart = {
    busy: false,
    cart: null,
    drawer: null,
    bound: false,
    editSession: null,

    beginEdit: function (lineKey, addonCandidates) {
      this.editSession = {
        lineKey: lineKey,
        addonCandidates: addonCandidates || [],
        addonKeys: []
      };
      return this.editSession;
    },

    replaceEditLines: function () {
      var session = this.editSession;
      this.editSession = null;
      if (!session || !session.lineKey) return Promise.resolve(null);
      var self = this;
      var keys = [session.lineKey].concat(session.addonKeys || []);
      var seen = {};
      var unique = [];
      keys.forEach(function (key) {
        if (!key || seen[key]) return;
        seen[key] = true;
        unique.push(key);
      });
      return unique.reduce(function (chain, key) {
        return chain.then(function () {
          return self.change(key, 0);
        });
      }, Promise.resolve());
    },

    formatMoney: function (cents) {
      var drawer = this.drawer || qs('[data-bn-cart-drawer]');
      var moneyFormat =
        (drawer && drawer.getAttribute('data-money-format')) ||
        (window.Shopify && Shopify.money_format) ||
        '{{amount}}';
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, moneyFormat);
      }
      var amount = (Number(cents) / 100).toFixed(2).replace('.', ',');
      return String(moneyFormat).replace(/\{\{\s*amount[^}]*\}\}/, amount);
    },

    updateBadges: function (count) {
      qsa('.bn-header__badge').forEach(function (badge) {
        if (count > 0) {
          badge.textContent = String(count);
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      });
    },

    setBusy: function (on) {
      this.busy = !!on;
      qsa('[data-bn-cart-busy]').forEach(function (el) {
        el.hidden = !on;
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
    },

    open: function () {
      var drawer = this.drawer || qs('[data-bn-cart-drawer]');
      if (!drawer) return;
      this.drawer = drawer;
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('bn-cart-drawer-open');
      // Double rAF so the closed transform paints before we animate open.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          drawer.classList.add('is-open');
        });
      });
    },

    close: function () {
      var drawer = this.drawer || qs('[data-bn-cart-drawer]');
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('bn-cart-drawer-open');
      window.setTimeout(function () {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 360);
    },

    get: function () {
      return fetch(rootPath() + 'cart.js', {
        headers: { Accept: 'application/json' }
      }).then(function (res) {
        if (!res.ok) throw new Error('cart_get_failed');
        return res.json();
      });
    },

    change: function (key, quantity) {
      return fetch(rootPath() + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      });
    },

    renderLine: function (item) {
      var fee = isFeeLineItem(item);
      var img = item.image
        ? '<img class="bn-cart-line__img" src="' +
          String(item.image).replace(/"/g, '&quot;') +
          '" alt="" width="64" height="64" loading="lazy">'
        : '<span class="bn-cart-line__img bn-cart-line__img--empty"></span>';
      var propsHtml = '';
      if (!fee) {
        var props = visibleProps(item.properties);
        if (props.length) {
          propsHtml =
            '<div class="bn-cart-line__props">' +
            props
              .map(function (p) {
                return (
                  '<span class="bn-cart-line__prop">' +
                  escapeHtml(p.key) +
                  ': ' +
                  escapeHtml(p.value) +
                  '</span>'
                );
              })
              .join('') +
            '</div>';
        }
      }
      var qtyHtml = fee
        ? ''
        : '<div class="bn-cart-qty" data-bn-cart-qty>' +
          '<button type="button" class="bn-cart-qty__btn" data-bn-cart-qty-change="-1" aria-label="Azalt">−</button>' +
          '<span class="bn-cart-qty__value" data-bn-cart-qty-value>' +
          item.quantity +
          '</span>' +
          '<button type="button" class="bn-cart-qty__btn" data-bn-cart-qty-change="1" aria-label="Artır">+</button>' +
          '</div>';
      var removeHtml = fee
        ? ''
        : '<button type="button" class="bn-cart-line__remove" data-bn-cart-remove aria-label="Ürünü kaldır">×</button>';
      var url = item.url || '#';
      var editHtml = fee
        ? ''
        : '<a href="' +
          escapeHtml(cartEditUrl(item)) +
          '" class="bn-cart-line__edit">Düzenle</a>';
      return (
        '<li class="bn-cart-line' +
        (fee ? ' bn-cart-line--fee' : '') +
        '" data-bn-cart-line data-line-key="' +
        escapeHtml(item.key) +
        '" data-line-qty="' +
        item.quantity +
        '"' +
        (fee ? ' data-fee-line="true"' : '') +
        '>' +
        '<a href="' +
        escapeHtml(url) +
        '" class="bn-cart-line__media">' +
        img +
        '</a>' +
        '<div class="bn-cart-line__main">' +
        '<a href="' +
        escapeHtml(url) +
        '" class="bn-cart-line__title">' +
        escapeHtml(item.product_title || item.title || '') +
        '</a>' +
        editHtml +
        propsHtml +
        '<p class="bn-cart-line__price">' +
        this.formatMoney(item.final_line_price) +
        '</p>' +
        qtyHtml +
        '</div>' +
        removeHtml +
        '</li>'
      );
    },

    renderDrawer: function (cart) {
      var drawer = this.drawer || qs('[data-bn-cart-drawer]');
      if (!drawer) return;
      this.drawer = drawer;
      var body = qs('[data-bn-cart-drawer-body]', drawer);
      var footer = qs('[data-bn-cart-footer]', drawer);
      var countEl = qs('[data-bn-cart-count]', drawer);
      var subtotalEl = qs('[data-bn-cart-subtotal]', drawer);
      var alerts = qs('[data-bn-cart-alerts]', drawer);
      var collectionsUrl = drawer.getAttribute('data-collections-url') || '/collections/all';
      var items = (cart && cart.items) || [];
      var count = (cart && cart.item_count) || 0;

      if (countEl) {
        countEl.textContent = count > 0 ? count + ' ürün' : '';
      }

      if (alerts) {
        var conflicts = cartConflicts(cart);
        if (conflicts.fulfillment || conflicts.store) {
          var parts = [];
          if (conflicts.fulfillment) {
            parts.push('<p>Aynı sepette hem mağazadan teslim hem adrese teslim ürün var.</p>');
          }
          if (conflicts.store) {
            parts.push('<p>Sepetteki ürünler farklı mağazalardan teslim için seçilmiş.</p>');
          }
          alerts.innerHTML = '<div class="bn-cart-alert" role="alert">' + parts.join('') + '</div>';
        } else {
          alerts.innerHTML = '';
        }
      }

      if (body) {
        if (!items.length) {
          body.innerHTML =
            '<div class="bn-cart-drawer__empty" data-bn-cart-empty>' +
            '<p>Sepetiniz boş.</p>' +
            '<a href="' +
            escapeHtml(collectionsUrl) +
            '" class="bn-cart-drawer__empty-cta" data-bn-cart-close>Alışverişe devam et</a>' +
            '</div>';
        } else {
          body.innerHTML =
            '<ul class="bn-cart-drawer__items" role="list" data-bn-cart-lines">' +
            items.map(this.renderLine.bind(this)).join('') +
            '</ul>';
        }
      }

      if (footer) footer.hidden = !items.length;
      if (subtotalEl) subtotalEl.textContent = this.formatMoney((cart && cart.total_price) || 0);
    },

    renderCartPage: function (cart) {
      var page = qs('[data-bn-cart-page]');
      if (!page) return;
      // Cart page has Liquid-computed fee split — safest refresh is reload.
      if (!cart || !cart.item_count) {
        window.location.reload();
        return;
      }
      var sectionId = page.getAttribute('data-section-id');
      if (!sectionId) {
        window.location.reload();
        return;
      }
      var self = this;
      return fetch(rootPath() + 'cart?section_id=' + encodeURIComponent(sectionId))
        .then(function (res) {
          if (!res.ok) throw new Error('section_failed');
          return res.text();
        })
        .then(function (html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var next = tmp.querySelector('[data-bn-main-cart]') || tmp.firstElementChild;
          var current = page.closest('[data-bn-main-cart]') || qs('[data-bn-main-cart]');
          if (next && current && current.parentNode) {
            current.parentNode.replaceChild(next, current);
            self.initCoupons();
          } else {
            window.location.reload();
          }
        })
        .catch(function () {
          window.location.reload();
        });
    },

    render: function (cart) {
      this.cart = cart;
      this.updateBadges((cart && cart.item_count) || 0);
      this.renderDrawer(cart);
    },

    refresh: function (opts) {
      var self = this;
      var open = opts && opts.open;
      return this.get()
        .then(function (cart) {
          self.render(cart);
          if (open) self.open();
          return cart;
        })
        .catch(function (err) {
          console.warn('[BnCart] refresh failed', err);
        });
    },

    applyChange: function (key, quantity) {
      if (this.busy || !key) return Promise.resolve();
      var self = this;
      this.setBusy(true);
      return this.change(key, quantity)
        .then(function (cart) {
          self.render(cart);
          return Promise.resolve(self.renderCartPage(cart)).then(function () {
            return cart;
          });
        })
        .catch(function (err) {
          console.warn('[BnCart] change failed', err);
          return self.refresh();
        })
        .finally(function () {
          self.setBusy(false);
        });
    },

    initCoupons: function () {
      qsa('[data-bn-main-cart]').forEach(function (root) {
        var input = qs('[data-bn-cart-coupon-input]', root);
        var applyBtn = qs('[data-bn-cart-coupon-apply]', root);
        if (!applyBtn || !input || applyBtn.getAttribute('data-bn-bound')) return;
        applyBtn.setAttribute('data-bn-bound', '1');
        applyBtn.addEventListener('click', function () {
          var code = input.value.trim();
          if (!code) {
            input.focus();
            return;
          }
          window.location.href =
            rootPath() +
            'discount/' +
            encodeURIComponent(code) +
            '?redirect=' +
            encodeURIComponent('/cart');
        });
        input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            applyBtn.click();
          }
        });
      });
    },

    init: function () {
      var self = this;
      this.drawer = qs('[data-bn-cart-drawer]');
      this.initCoupons();
      if (this.bound) return;
      this.bound = true;

      document.addEventListener('click', function (e) {
        var openBtn = e.target.closest('[data-bn-cart-open]');
        if (openBtn) {
          // On the cart page, never open the drawer — let the /cart link work.
          if (qs('[data-bn-cart-page]') || /\/cart\/?$/.test(window.location.pathname || '')) {
            return;
          }
          e.preventDefault();
          self.refresh({ open: true });
          return;
        }

        var closeBtn = e.target.closest('[data-bn-cart-close]');
        if (closeBtn) {
          if (closeBtn.tagName === 'A' && closeBtn.getAttribute('href') && closeBtn.getAttribute('href') !== '#') {
            self.close();
            return;
          }
          e.preventDefault();
          self.close();
          return;
        }

        if (self.busy) {
          if (e.target.closest('[data-bn-cart-qty-change], [data-bn-cart-remove]')) {
            e.preventDefault();
          }
          return;
        }

        var qtyBtn = e.target.closest('[data-bn-cart-qty-change]');
        if (qtyBtn) {
          e.preventDefault();
          var line = qtyBtn.closest('[data-bn-cart-line]');
          if (!line || line.getAttribute('data-fee-line') === 'true') return;
          var key = line.getAttribute('data-line-key');
          var current = parseInt(line.getAttribute('data-line-qty') || '1', 10) || 1;
          var delta = parseInt(qtyBtn.getAttribute('data-bn-cart-qty-change') || '0', 10) || 0;
          var next = Math.max(0, current + delta);
          self.applyChange(key, next);
          return;
        }

        var removeBtn = e.target.closest('[data-bn-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          var removeLine = removeBtn.closest('[data-bn-cart-line]');
          if (!removeLine || removeLine.getAttribute('data-fee-line') === 'true') return;
          self.applyChange(removeLine.getAttribute('data-line-key'), 0);
        }
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.drawer && !self.drawer.hidden) {
          self.close();
        }
      });

      document.addEventListener('bn:cart:updated', function (e) {
        var detail = (e && e.detail) || {};
        var onCartPage =
          !!qs('[data-bn-cart-page]') || /\/cart\/?$/.test(window.location.pathname || '');
        // A fresh add-to-cart gets the overlay fork instead of the drawer.
        var justAdded = !onCartPage && detail.open === true;
        var overlay = justAdded ? qs('[data-bn-atc-overlay]') : null;
        self.refresh({ open: !onCartPage && !overlay && detail.open !== false });
        if (overlay) openAtcOverlay(overlay);
      });

      window.BnCart = self;
    }
  };

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function initBnCart() {
    BnCart.init();
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function initBnTheme() {
    document.body.classList.add('bn-theme');
    initPageMotion();
    initOrderTracking();
    initMobileMenu();
    initPredictiveSearch();
    initMobileSearch();
    initLogoFallback();
    initCarousels();
    initScrollTop();
    initStickyHeader();
    initSectionReveal();
    initProductCardReveal();
    initProductPage();
    initProductCardImages();
    initBnCart();
    initAtcOverlay();
  }

  ready(initBnTheme);

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('[data-bn-header]')) {
      initMobileMenu();
      initPredictiveSearch();
      initMobileSearch();
    }
    initCarousels();
    initSectionReveal();
    initProductCardReveal(event.target);
    initProductCardImages();
    if (event.target && event.target.querySelector && event.target.querySelector('[data-bn-product]')) {
      initProductPage();
    }
    if (event.target && event.target.querySelector && event.target.querySelector('[data-bn-main-cart]')) {
      initBnCart();
    }
  });
})();
